// A course's lessons, as the pages show them: what each is called, how far
// you are with it, which comes next, and the line a lesson plays on the
// board. No DOM and no requests — the lesson screen (Lesson.tsx) and the
// repertoire page draw from here — so it is all played in Node
// (tests/web/lessons.test.mjs). The lessons themselves are chosen by the
// server (OPENINGS.md §5.5).

import { Chess } from "chess.js";

export type Moment =
  | { kind: "key" }
  | { kind: "punish" }
  | { kind: "sacrifice"; cents: number }
  | { kind: "only"; second: string };

export interface LessonStep {
  san: string;
  uci: string;
  /** The position the move is played in, as an EPD. */
  before: string;
  fen: string;
  yours: boolean;
  note?: string;
  moment?: Moment;
}

export interface Lesson {
  kind: "opening" | "reply" | "mistake";
  reply: string | null;
  share: number | null;
  steps: LessonStep[];
  key: string;
  watched: boolean;
  tried: boolean;
  /** Your positions in the line that are cards, and how many are drilled. */
  cards: number;
  drilled: number;
  /** For a mistake: what punishing it comes to. */
  punish?: { wins: { kind: "mate" } | { kind: "material"; cents: number } | { kind: "position" }; motifs: string[]; white_eval: import("../board/scores.ts").Score };
}

export interface Lessons {
  way_in: LessonStep[];
  branch: string;
  list: Lesson[];
}

/** How a lesson is played: shown first then asked, or all from memory. */
export type Mode = "watch" | "try";

/** Where you are with a lesson. Learned is drilled: every card of it
 *  answered at least once. */
export type Stage = "new" | "watched" | "tried" | "learned";

export function stageOf(lesson: Lesson): Stage {
  if (lesson.tried && lesson.drilled >= lesson.cards) return "learned";
  if (lesson.tried) return "tried";
  if (lesson.watched) return "watched";
  return "new";
}

/** What to do with a lesson next, and the mode it is done in. */
export function nextStep(lesson: Lesson): { label: string; mode: Mode | "drill" } | null {
  switch (stageOf(lesson)) {
    case "new":
      return { label: "Watch", mode: "watch" };
    case "watched":
      return { label: "Try it", mode: "try" };
    case "tried":
      return { label: "Drill it", mode: "drill" };
    default:
      return null;
  }
}

/** The lesson to go on with: the first not yet played from memory, in
 *  order — most met first. Drilling is the drill's, on its own schedule, and
 *  does not hold the next lesson back; once every lesson is tried, the first
 *  not drilled. Null when all are learned. */
export function current(list: Lesson[]): number | null {
  const untried = list.findIndex((l) => !l.tried);
  if (untried >= 0) return untried;
  const i = list.findIndex((l) => stageOf(l) !== "learned");
  return i < 0 ? null : i;
}

/** "3.", "3…" for the move played in a FEN's position. */
export function moveLabel(fen: string): string {
  const parts = String(fen).split(/\s+/);
  const n = Number(parts[5]) || 1;
  return `${n}${parts[1] === "b" ? "…" : "."}`;
}

/** "3…Nc6" */
export const said = (step: LessonStep): string => `${moveLabel(step.fen)}${step.san}`;

/** What a lesson is called. */
export function titleOf(lesson: Lesson, name: string): string {
  const first = lesson.steps[0];
  if (lesson.kind === "opening" || !first) return name;
  return lesson.kind === "mistake" ? `${said(first)}?` : said(first);
}

/** What a lesson is, under its title. */
export function subtitleOf(lesson: Lesson): string {
  if (lesson.kind === "opening") return "The moves in, and why";
  const share = lesson.share != null ? `${Math.max(1, Math.round(lesson.share * 100))}% of games at your level` : "";
  if (lesson.kind === "mistake") return ["Where they go wrong", share].filter(Boolean).join(" · ");
  return share || "A reply they play";
}

/** The line a lesson puts on the board: the puzzle starts at your first
 *  move, and their moves before it — the reply — are said, not asked. */
export function boardLine(lesson: Lesson): { fen: string; moves: string; lead: LessonStep[]; steps: LessonStep[] } | null {
  const first = lesson.steps.findIndex((s) => s.yours);
  if (first < 0) return null;
  const steps = lesson.steps.slice(first);
  return { fen: steps[0]!.fen, moves: steps.map((s) => s.uci).join(","), lead: lesson.steps.slice(0, first), steps };
}

/** The moves that lead to a lesson, as a line of text: "1.e4 e5 2.f4 exf4 3.Nf3". */
export function pathText(steps: LessonStep[]): string {
  return steps
    .map((s, i) => {
      const label = moveLabel(s.fen);
      if (label.endsWith(".")) return label + s.san;
      return i === 0 ? label.replace("…", "...") + s.san : s.san;
    })
    .join(" ");
}

/** Where watching a lesson is: watching, the line is walked a move at a
 *  time, back and on, like the repertoire's line. At `at` (0 to the number
 *  of steps) the board has the position before step `at`; `played` is the
 *  move that led there and `next` the one about to be played — the one the
 *  arrow and the sheet are about. */
export interface WatchAt {
  at: number;
  fen: string;
  played: LessonStep | null;
  next: LessonStep | null;
  canBack: boolean;
  canOn: boolean;
}

export function watchAt(steps: LessonStep[], at: number): WatchAt {
  const i = Math.max(0, Math.min(steps.length, Math.trunc(at) || 0));
  const next = steps[i] ?? null;
  const played = i > 0 ? steps[i - 1]! : null;
  let fen = next ? next.fen : "";
  if (!next && played) {
    const game = new Chess(played.fen);
    game.move({ from: played.uci.slice(0, 2), to: played.uci.slice(2, 4), promotion: played.uci[4] || "q" });
    fen = game.fen();
  }
  return { at: i, fen, played, next, canBack: i > 0, canOn: i < steps.length };
}

/** Which lessons are watched as a line walked back and on: all but a
 *  mistake's, whose punishment is found, never shown. */
export function watchedAsLine(lesson: Lesson, mode: Mode): boolean {
  return mode === "watch" && lesson.kind !== "mistake";
}

/** What is said before a moment is asked. */
export function askText(moment: Moment | undefined, lesson: Lesson): string {
  if (lesson.kind === "mistake" && moment?.kind !== "punish") return "Keep going: find the next move.";
  switch (moment?.kind) {
    case "key":
      return "A key move. What do you play here?";
    case "punish":
      return "A mistake. Find the move that punishes it.";
    case "sacrifice":
      return "The attack costs material here. Find the move that gives it.";
    case "only":
      return "Only one move keeps the thread here. Find it.";
    default:
      return "Your move.";
  }
}

/** A moment's name, for its badge. */
export function momentName(moment: Moment): string {
  switch (moment.kind) {
    case "key":
      return "Key move";
    case "punish":
      return "Punish it";
    case "sacrifice":
      return moment.cents >= 300 ? "Piece sacrifice" : moment.cents >= 200 ? "Sacrifice" : "Pawn sacrifice";
    case "only":
      return "Only move";
  }
}

/** What a moment was, said once it is played. */
export function afterText(moment: Moment): string {
  switch (moment.kind) {
    case "only":
      return `The next best, ${moment.second}, lets the advantage go.`;
    case "sacrifice": {
      const c = moment.cents;
      const what = c >= 900 ? "a queen" : c >= 500 ? "a rook" : c >= 300 ? "a piece" : c >= 200 ? `${Math.round(c / 100)} pawns` : "a pawn";
      return `${what[0]!.toUpperCase()}${what.slice(1)} given for time and open lines.`;
    }
    default:
      return "";
  }
}

/** What to take away: the moments of your line, each with its note. */
export function ideas(lesson: Lesson): { label: string; moment: Moment; note?: string }[] {
  return lesson.steps.filter((s) => s.yours && s.moment).map((s) => ({ label: said(s), moment: s.moment!, note: s.note }));
}

/** "2 of 7 learned" and the like, for the course page. */
export function courseProgress(list: Lesson[]): { learned: number; started: number; total: number } {
  return {
    learned: list.filter((l) => stageOf(l) === "learned").length,
    started: list.filter((l) => stageOf(l) !== "new").length,
    total: list.length,
  };
}

/** Where the drill of a lesson starts: your first position in its line. */
export function drillFrom(lesson: Lesson): string | null {
  return lesson.steps.find((s) => s.yours)?.before ?? null;
}

const PIECE: Record<string, string> = { p: "pawn", n: "knight", b: "bishop", r: "rook", q: "queen", k: "king" };
const WORTH: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };

/**
 * What a move plainly does, for a move no note explains: castles, takes
 * back, develops, takes the centre, attacks a piece, gives check. Only what
 * the board says for certain — the why is the notes' — and null when it
 * says nothing worth a line.
 */
export function describe(fen: string, uci: string, previous?: string): string | null {
  const game = new Chess(fen);
  const move = game.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] || "q" });
  if (!move) return null;
  const said: string[] = [];
  if (move.flags.includes("k") || move.flags.includes("q")) said.push("Castles: the king safe, a rook towards the centre.");
  else if (move.captured) {
    const back = previous && previous.slice(2, 4) === move.to;
    said.push(back ? `Takes back on ${move.to}.` : `Takes the ${PIECE[move.captured]} on ${move.to}.`);
  } else if ((move.piece === "n" || move.piece === "b") && (move.from[1] === "1" || move.from[1] === "8")) said.push(`Develops the ${PIECE[move.piece]}.`);
  else if (move.piece === "p" && ["d4", "e4", "d5", "e5"].includes(move.to)) said.push("Takes a square in the centre.");
  // What the piece that moved attacks now: a piece worth at least as much,
  // or one nothing defends.
  if (!game.in_check()) {
    const parts = game.fen().split(" ");
    parts[1] = parts[1] === "w" ? "b" : "w";
    parts[3] = "-";
    const again = new Chess(parts.join(" "));
    const hits = again
      .moves({ square: move.to, verbose: true })
      .filter((m) => m.captured && m.captured !== "p" && m.captured !== "k")
      .filter((m) => WORTH[m.captured!]! >= WORTH[move.piece]! || !defended(game, m.to));
    const target = hits.sort((a, b) => WORTH[b.captured!]! - WORTH[a.captured!]!)[0];
    if (target) said.push(`It attacks the ${PIECE[target.captured!]} on ${target.to}.`);
  } else said.push("Check.");
  return said.length ? said.join(" ") : null;
}

/** Whether the side to move in `game` defends `square`: whether anything
 *  of theirs could take a piece of the other side standing there. */
function defended(game: Chess, square: string): boolean {
  const parts = game.fen().split(" ");
  const rows = parts[0]!.split("/").map((row) => row.replace(/\d/g, (n) => ".".repeat(Number(n))).split(""));
  const file = square.charCodeAt(0) - 97;
  const rank = 8 - Number(square[1]);
  const here = rows[rank]?.[file];
  if (!here || here === ".") return false;
  // A queen of the other side in its place: taking it is defending it.
  rows[rank]![file] = here === here.toUpperCase() ? "q" : "Q";
  parts[0] = rows.map((row) => row.join("").replace(/\.+/g, (dots) => String(dots.length))).join("/");
  parts[3] = "-";
  const probe = new Chess();
  if (!probe.load(parts.join(" "))) return false;
  return probe.moves({ verbose: true }).some((m) => m.to === square);
}
